import React, { useState } from 'react';
import {
  Calendar, Eye, FileText, Mail, MousePointerClick, TrendingUp, Users,
} from 'lucide-react';
import { KpiCard, LoadingSkeleton } from './_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConversionsTab({ data }) {
  const [digestStatus, setDigestStatus] = useState('idle'); // idle | loading | loaded | error
  const [digest, setDigest] = useState(null);

  const fetchDigest = async (sendEmail = false) => {
    setDigestStatus('loading');
    try {
      const token = sessionStorage.getItem('admin_token');
      const resp = await fetch(`${API_URL}/api/admin/weekly-digest?send_email=${sendEmail}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed');
      const d = await resp.json();
      setDigest(d);
      setDigestStatus('loaded');
    } catch {
      setDigestStatus('error');
      setTimeout(() => setDigestStatus('idle'), 3000);
    }
  };

  if (!data) return <LoadingSkeleton />;
  const conversions = data.conversions || [];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Eye} label="Total Article Views" value={data.total_views?.toLocaleString() || 0} color="#10B981" />
        <KpiCard icon={MousePointerClick} label="Affiliate Clicks" value={data.total_clicks?.toLocaleString() || 0} color="#06B6D4" />
        <KpiCard icon={TrendingUp} label="Avg Conversion Rate" value={`${data.avg_conversion_rate || 0}%`} subtext="views to clicks" color="#F59E0B" />
        <KpiCard icon={FileText} label="Articles with Clicks" value={conversions.filter(c => c.clicks > 0).length} color="#8B5CF6" />
      </div>

      {/* Weekly Digest Panel */}
      <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="weekly-digest-panel">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-amber-500" /> Weekly Performance Digest
          </h3>
          <div className="flex gap-2">
            <button
              onClick={() => fetchDigest(false)}
              disabled={digestStatus === 'loading'}
              className="px-3 py-1.5 text-xs font-medium bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
              data-testid="generate-digest-btn"
            >
              {digestStatus === 'loading' ? 'Generating...' : 'Generate Digest'}
            </button>
            <button
              onClick={() => fetchDigest(true)}
              disabled={digestStatus === 'loading'}
              className="px-3 py-1.5 text-xs font-medium bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors border border-teal-200"
              data-testid="send-digest-email-btn"
            >
              <Mail className="w-3 h-3 inline mr-1" />Email Digest
            </button>
          </div>
        </div>
        {digest && digestStatus === 'loaded' && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-3" data-testid="digest-results">
            <p className="text-xs text-gray-500 font-medium">{digest.period}</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { label: 'Article Views', value: digest.summary.article_views, change: digest.summary.article_views_change },
                { label: 'Affiliate Clicks', value: digest.summary.affiliate_clicks, change: digest.summary.affiliate_clicks_change },
                { label: 'Conv. Rate', value: digest.summary.conversion_rate },
                { label: 'New Users', value: digest.summary.new_users },
                { label: 'New Reviews', value: digest.summary.new_reviews },
              ].map((m, i) => (
                <div key={m.label} className="bg-white p-2.5 rounded border border-gray-100">
                  <p className="text-lg font-bold text-gray-900">{m.value}</p>
                  <p className="text-[10px] text-gray-400">{m.label} {m.change && <span className={m.change?.startsWith('+') ? 'text-green-500' : 'text-red-500'}>{m.change}</span>}</p>
                </div>
              ))}
            </div>
            {digest.top_articles?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">Top Articles This Week</p>
                {digest.top_articles.map((a, i) => (
                  <p key={a.title} className="text-xs text-gray-500">{i+1}. {a.title} <span className="text-gray-400">({a.views} views)</span></p>
                ))}
              </div>
            )}
            {digest.email_sent && <p className="text-xs text-green-500 font-medium">Digest email sent successfully!</p>}
            {digest.email_error && <p className="text-xs text-red-400">{digest.email_error}</p>}
          </div>
        )}
        {digestStatus === 'error' && <p className="text-xs text-red-400">Failed to generate digest. Please try again.</p>}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="conversions-table">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-teal-500" /> Article Conversion Attribution
        </h3>
        <p className="text-xs text-gray-400 mb-4">Which articles drive the most affiliate clicks? Higher conversion rate = better monetization potential.</p>
        {conversions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 text-xs border-b border-gray-100">
                  <th className="pb-2 font-medium">#</th>
                  <th className="pb-2 font-medium">Article / Page</th>
                  <th className="pb-2 font-medium text-right">Views</th>
                  <th className="pb-2 font-medium text-right">Clicks</th>
                  <th className="pb-2 font-medium text-right">Conv. Rate</th>
                  <th className="pb-2 font-medium">Top Products</th>
                </tr>
              </thead>
              <tbody>
                {conversions.map((c, i) => (
                  <tr key={c.title} className="border-b border-gray-50 last:border-0" data-testid={`conversion-row-${i}`}>
                    <td className="py-2.5 text-gray-300 text-xs font-mono">{i + 1}</td>
                    <td className="py-2.5 text-gray-700 text-xs max-w-[250px] truncate" title={c.page}>{c.title || c.page}</td>
                    <td className="py-2.5 text-right font-mono text-gray-500">{c.views?.toLocaleString()}</td>
                    <td className="py-2.5 text-right font-mono text-gray-700 font-medium">{c.clicks?.toLocaleString()}</td>
                    <td className="py-2.5 text-right">
                      <span className={`px-2 py-0.5 text-xs rounded-full font-mono ${
                        c.conversion_rate >= 30 ? 'bg-green-50 text-green-600'
                        : c.conversion_rate >= 10 ? 'bg-yellow-50 text-yellow-600'
                        : 'bg-gray-50 text-gray-500'
                      }`}>
                        {c.conversion_rate}%
                      </span>
                    </td>
                    <td className="py-2.5 text-gray-400 text-[10px] max-w-[180px] truncate">{(c.top_products || []).join(', ') || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-gray-400 text-sm">No conversion data yet. Data will appear as users view articles and click affiliate links.</p>}
      </div>
    </div>
  );
}
